import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx'; 
import './App.css';

function App() {
  // Auth states
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  // Hardcoded users with credentials as specified
  const users = [
    { username: 'nishant', password: 'nishant@deepsight5555', name: 'Nishant', role: 'admin' },
    { username: 'deepsight', password: '5555@deepsight', name: 'DeepSight User', role: 'user' },
    { username: 'admin', password: 'admin', name: 'DeepSight User', role: 'user' }
  ];
  
  // Check for existing login
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      setCurrentUser(JSON.parse(user));
      setIsLoggedIn(true);
    }
  }, []);
  
  // Handle login - case insensitive for username
  const handleLogin = (e) => {
    e.preventDefault();
    
    const matchingUser = users.find(
      user => user.username.toLowerCase() === username.toLowerCase() && user.password === password
    );
    
    if (matchingUser) {
      const userInfo = {
        name: matchingUser.name,
        username: matchingUser.username,
        role: matchingUser.role
      };
      
      localStorage.setItem('user', JSON.stringify(userInfo));
      setCurrentUser(userInfo);
      setIsLoggedIn(true);
      setLoginError('');
    } else {
      setLoginError('Invalid username or password');
    }
  };
  
  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('user');
    setCurrentUser(null);
    setIsLoggedIn(false);
    setShowUserMenu(false);
  };
  
  return (
    <div className="app-container">
      {isLoggedIn ? (
        <div>
          {/* Header */}
          <div className="header">
            <div className="header-content">
              <h1 className="header-title">DeepSight AI Labs</h1>
              <div className="header-actions">
                <button 
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="user-menu-btn"
                >
                  <span className="user-welcome">Welcome, {currentUser?.name}</span>
                  <svg className="dropdown-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* User dropdown menu */}
                {showUserMenu && (
                  <div className="user-dropdown">
                    <div className="user-info">
                      <div className="user-name">{currentUser?.username}</div>
                      <div className="user-role">{currentUser?.name}</div>
                    </div>
                    
                    <button 
                      onClick={handleLogout}
                      className="dropdown-logout-btn"
                    >
                      Logout
                    </button>
                  </div>
                )}
                
                <button 
                  onClick={handleLogout} 
                  className="logout-btn"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
          
          {/* Main calculator */}
          <div className="main-content">
            <AWSCalculator />
          </div>
        </div>
      ) : (
        // Login Screen
        <div className="login-screen">
          <div className="login-card">
            <div className="login-header">
              <h2 className="login-title">DeepSight AI Labs</h2>
              <p className="login-subtitle">AWS Instance Calculator</p>
            </div>
            
            <form onSubmit={handleLogin}>
              {loginError && (
                <div className="login-error">
                  {loginError}
                </div>
              )}
              
              <div className="form-group">
                <label className="form-label">Username</label>
                <input 
                  type="text" 
                  value={username} 
                  onChange={(e) => setUsername(e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Password</label>
                <input 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              
              <button 
                type="submit" 
                className="login-submit-btn"
              >
                Login
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// AWS Calculator Component
function AWSCalculator() {
  const [totalCameras, setTotalCameras] = useState("");
  const [inmCameras, setInmCameras] = useState("");
  const [groupNumber, setGroupNumber] = useState(3);
  const [processorType, setProcessorType] = useState("intel");
  const [results, setResults] = useState(null);
  const [copyStatus, setCopyStatus] = useState("");
  const [isCalculating, setIsCalculating] = useState(false);
  const [showInstanceTypes, setShowInstanceTypes] = useState(false);
  
  // Generate AI group options
  const groupOptions = [1, 2, 3, 4, 5].map(num => ({
    value: num,
    label: `AI-group${num}`
  }));
  
  // Calculate object detection features
  const objDetectionFeatures = (parseInt(totalCameras) || 0) - (parseInt(inmCameras) || 0);
  
  // Get instance prefix based on processor type
  const getInstancePrefix = () => {
    return processorType === "intel" ? "c6i" : "c6a";
  };
  
  // Instance types - CORRECTED C6a specifications
  const getInstanceTypes = () => {
    const prefix = getInstancePrefix();
    
    if (processorType === "intel") {
      return [
        { type: `${prefix}.large`, vCPUs: 2, memory: 4, cores: 2 },
        { type: `${prefix}.xlarge`, vCPUs: 4, memory: 8, cores: 4 },
        { type: `${prefix}.2xlarge`, vCPUs: 8, memory: 16, cores: 8 },
        { type: `${prefix}.4xlarge`, vCPUs: 16, memory: 32, cores: 16 },
        { type: `${prefix}.8xlarge`, vCPUs: 32, memory: 64, cores: 32 },
        { type: `${prefix}.12xlarge`, vCPUs: 48, memory: 96, cores: 48 },
        { type: `${prefix}.16xlarge`, vCPUs: 64, memory: 128, cores: 64 },
        { type: `${prefix}.24xlarge`, vCPUs: 96, memory: 192, cores: 96 },
        { type: `${prefix}.32xlarge`, vCPUs: 128, memory: 256, cores: 128 },
        { type: `${prefix}.metal`, vCPUs: 128, memory: 256, cores: 128 }
      ];
    } else {
      // AMD C6a - Different specifications for 32xlarge, 48xlarge, metal
      return [
        { type: `${prefix}.large`, vCPUs: 2, memory: 4, cores: 2 },
        { type: `${prefix}.xlarge`, vCPUs: 4, memory: 8, cores: 4 },
        { type: `${prefix}.2xlarge`, vCPUs: 8, memory: 16, cores: 8 },
        { type: `${prefix}.4xlarge`, vCPUs: 16, memory: 32, cores: 16 },
        { type: `${prefix}.8xlarge`, vCPUs: 32, memory: 64, cores: 32 },
        { type: `${prefix}.12xlarge`, vCPUs: 48, memory: 96, cores: 48 },
        { type: `${prefix}.16xlarge`, vCPUs: 64, memory: 128, cores: 64 },
        { type: `${prefix}.24xlarge`, vCPUs: 96, memory: 192, cores: 96 },
        { type: `${prefix}.32xlarge`, vCPUs: 128, memory: 256, cores: 128 }
        
      ];
    }
  };
  
  // Calculate instances based on cores
  const calculateInstances = (totalCores) => {
    const instances = {};
    const prefix = getInstancePrefix();
    instances["Master"] = `${prefix}.4xlarge`;
    
    let remainingCores = totalCores;
    let slaveCount = 1;
    
    const instanceTypes = getInstanceTypes();
    const sortedTypes = [...instanceTypes].sort((a, b) => b.cores - a.cores);
    
    while (remainingCores > 0) {
      let selectedType = `${prefix}.4xlarge`;
      let selectedCores = 16;
      
      if (remainingCores > 16) {
        for (const instance of sortedTypes) {
          if (instance.cores <= remainingCores && instance.cores >= 16) {
            selectedType = instance.type;
            selectedCores = instance.cores;
            break;
          }
        }
      }
      
      instances[`Slave${slaveCount}`] = selectedType;
      remainingCores -= selectedCores;
      slaveCount++;
      
      if (slaveCount > 1000) break;
    }
    
    const totalSlaves = Object.keys(instances).filter(key => key.startsWith("Slave")).length;
    for (let i = totalSlaves + 1; i <= 4; i++) {
      instances[`Slave${i}`] = `${prefix}.4xlarge`;
    }
    
    return instances;
  };
  
  // Calculate downscale instances
  const calculateDownscaleInstances = (count = 5) => {
    const instances = {};
    const prefix = getInstancePrefix();
    instances["Master"] = `${prefix}.4xlarge`;
    
    for (let i = 1; i < count; i++) {
      instances[`Slave${i}`] = `${prefix}.4xlarge`;
    }
    
    return instances;
  };
  
  // Run calculations - CORRECTED LOGIC
  const runCalculations = () => {
    setIsCalculating(true);
    setShowInstanceTypes(true);
    
    setTimeout(() => {
      const totalCamerasValue = parseInt(totalCameras) || 0;
      const inmCamerasValue = parseInt(inmCameras) || 0;
      
      // ========================================
      // LEVEL 1: WITHOUT INM
      // Uses TOTAL cameras but WITHOUT INM features
      // ========================================
      
      // For Level 1: All cameras are treated as object detection cameras
      const level1_objDetectionCameras = totalCamerasValue;
      
      // Tamper Cores = Total Cameras * 2/6
      const level1_tamperCores = totalCamerasValue * (2/6);
      
      // Object Detection (ZI, CD, Trunk Features) = Total Cameras * 2/5
      const level1_objDetectionCores = totalCamerasValue * (2/5);
      const level1_objDetectionPodsRequired = Math.ceil(totalCamerasValue / 5);
      
      // Total cores for Level 1 (no inference, no INM)
      const level1Cores = level1_objDetectionCores + level1_tamperCores;
      
      // ========================================
      // LEVEL 2: WITH INM
      // Uses TOTAL cameras AND INM cameras
      // ========================================
      
      // INM Cores = INM Cameras * 2/3
      const inmCores = inmCamerasValue * (2/3);
      const inmPodsRequired = Math.ceil(inmCamerasValue / 2);
      
      // Object Detection cameras = Total - INM (for display only)
      const level2_objDetectionCameras = totalCamerasValue - inmCamerasValue;
      
      // Tamper Cores = Total Cameras * 2/6
      const level2_tamperCores = totalCamerasValue * (2/6);
      
      // CORRECTED: ZI, CD, Trunk Features = TOTAL Cameras * 2/5 (not just obj detection cameras)
      const level2_objDetectionCores = totalCamerasValue * (2/5);
      const level2_objDetectionPodsRequired = Math.ceil(totalCamerasValue / 5);
      
      // Total cores without inference
      const totalCoresWithoutInference = inmCores + level2_tamperCores + level2_objDetectionCores;
      
      // Approx value = total cores without inference / 128
      const approxValue = totalCoresWithoutInference / 128;
      
      // Inference calculations
      const inference1Cores = 6 * approxValue;
      const inference3Cores = 6 * approxValue;
      const tritionCores = 2 * approxValue;
      const kubernetsCores = 3 * approxValue;
      
      // Total cores for Level 2
      const level2TotalCores = totalCoresWithoutInference + inference1Cores + 
                        inference3Cores + tritionCores + kubernetsCores;
      
      // ========================================
      // Generate instance recommendations
      // ========================================
      const level1Instances = calculateInstances(level1Cores);
      const level2Instances = calculateInstances(level2TotalCores);
      const level3Instances = calculateDownscaleInstances(5);
      
      // Format lists
      const formatInstanceList = (instances, groupNum) => {
        return Object.entries(instances).map(([role, type]) => 
          `AI-group${groupNum}-${role} ${type}`
        );
      };
      
      const level1InstancesList = formatInstanceList(level1Instances, groupNumber);
      const level2InstancesList = formatInstanceList(level2Instances, groupNumber);
      const level3InstancesList = formatInstanceList(level3Instances, groupNumber);
      
      // Set results
      setResults({
        calculations: {
          // Level 1 data
          level1_totalCameras: totalCamerasValue,
          level1_objDetectionCameras: level1_objDetectionCameras,
          level1_objDetectionPodsRequired: level1_objDetectionPodsRequired,
          level1_objDetectionCores: level1_objDetectionCores,
          level1_tamperCores: level1_tamperCores,
          level1_totalCores: level1Cores,
          level1NodesRequired: Object.keys(level1Instances).length - 1,
          
          // Level 2 data
          level2_totalCameras: totalCamerasValue,
          inmCameras: inmCamerasValue,
          inmPodsRequired,
          inmCores,
          level2_objDetectionCameras: level2_objDetectionCameras,
          level2_objDetectionPodsRequired: level2_objDetectionPodsRequired,
          level2_objDetectionCores: level2_objDetectionCores,
          level2_tamperCores: level2_tamperCores,
          totalCoresWithoutInference,
          approxValue,
          inference1Cores,
          inference3Cores,
          tritionCores,
          kubernetsCores,
          level2_totalCores: level2TotalCores,
          level2NodesRequired: Object.keys(level2Instances).length - 1
        },
        recommendations: {
          level1: level1Instances,
          level1List: level1InstancesList,
          level2: level2Instances,
          level2List: level2InstancesList,
          level3: level3Instances,
          level3List: level3InstancesList
        }
      });
      
      setIsCalculating(false);
    }, 1000);
  };
  
  // Copy to clipboard
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopyStatus("Copied!");
        setTimeout(() => setCopyStatus(""), 2000);
      })
      .catch(() => {
        setCopyStatus("Copy failed");
        setTimeout(() => setCopyStatus(""), 2000);
      });
  };
  
  // Export to PDF
  const exportToPDF = () => {
    if (!results) {
      setCopyStatus("Please calculate results first");
      setTimeout(() => setCopyStatus(""), 2000);
      return;
    }
    
    const pdfContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>AWS Instance Calculator Report</title>
        <style>
          body { font-family: Arial; padding: 20px; }
          h1 { color: #0066cc; border-bottom: 2px solid #0066cc; }
          h2 { color: #333; margin-top: 20px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
          .level-header { background-color: #e8f4f8; padding: 10px; margin: 15px 0; font-weight: bold; }
        </style>
      </head>
      <body>
        <h1>AWS Instance Calculator Report</h1>
        <p><strong>Processor Type:</strong> ${processorType === 'intel' ? 'Intel (C6i)' : 'AMD (C6a)'}</p>
        
        <h2>Input Parameters</h2>
        <table>
          <tr><th>Parameter</th><th>Value</th></tr>
          <tr><td>Total Cameras</td><td>${totalCameras || 0}</td></tr>
          <tr><td>INM Cameras</td><td>${inmCameras || 0}</td></tr>
          <tr><td>Group</td><td>AI-group${groupNumber}</td></tr>
        </table>
        
        <div class="level-header">[Level -1] WITHOUT INM</div>
        <table>
          <tr><th>Instance</th></tr>
          ${results.recommendations.level1List.map(instance => 
            `<tr><td>${instance}</td></tr>`
          ).join('')}
        </table>
        
        <div class="level-header">[Level -2] WITH INM</div>
        <table>
          <tr><th>Instance</th></tr>
          ${results.recommendations.level2List.map(instance => 
            `<tr><td>${instance}</td></tr>`
          ).join('')}
        </table>
        
        <div class="level-header">[Level -3] Downscale Instances</div>
        <table>
          <tr><th>Instance</th></tr>
          ${results.recommendations.level3List.map(instance => 
            `<tr><td>${instance}</td></tr>`
          ).join('')}
        </table>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(pdfContent);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
      setCopyStatus("PDF generated");
      setTimeout(() => setCopyStatus(""), 2000);
    }, 500);
  };
  
  
// Export to Excel - Simple format (only instance lists)
const exportToExcel = () => {
  if (!results) {
    setCopyStatus('Please calculate results first');
    setTimeout(() => setCopyStatus(''), 2000);
    return;
  }

  // Create single consolidated data array
  const consolidatedData = [
    ['AWS INSTANCE CALCULATOR REPORT'],
    [],
    ['Processor Type', processorType === 'intel' ? 'Intel C6i' : 'AMD C6a'],
    [],
    ['INPUT PARAMETERS'],
    ['Total Cameras', totalCameras || 0],
    ['INM Cameras', inmCameras || 0],
    ['Group', `AI-group${groupNumber}`],
    [],
    ['[LEVEL -1] WITHOUT INM']
  ];

  // Add Level 1 instances
  results.recommendations.level1List.forEach(instance => {
    consolidatedData.push([instance]);
  });

  // Add Level 2 section
  consolidatedData.push([]);
  consolidatedData.push(['[LEVEL -2] WITH INM']);
  
  // Add Level 2 instances
  results.recommendations.level2List.forEach(instance => {
    consolidatedData.push([instance]);
  });

  // Add Level 3 section
  consolidatedData.push([]);
  consolidatedData.push(['[LEVEL -3] DOWNSCALE INSTANCES']);
  
  // Add Level 3 instances
  results.recommendations.level3List.forEach(instance => {
    consolidatedData.push([instance]);
  });

  // Create workbook and single sheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(consolidatedData);

  // Set column widths
  ws['!cols'] = [
    { wch: 35 }
  ];

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'AWS Calculator Report');

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  const filename = `AWS_Calculator_Report_${timestamp}.xlsx`;

  // Write file
  XLSX.writeFile(wb, filename);

  setCopyStatus('Excel file downloaded');
  setTimeout(() => setCopyStatus(''), 2000);
};


  
  return (
    <div className="calculator-container">
      <div className="calculator-card">
        <h2 className="calculator-title">AWS Instance Calculator</h2>
        
        {/* Input form */}
        <div className="input-section">
          <h3 className="section-title">Input Parameters</h3>
          
          <div className="input-grid">
            <div className="form-group">
              <label className="form-label">Total Cameras</label>
              <input 
                type="number" 
                className="form-input" 
                value={totalCameras}
                onChange={(e) => setTotalCameras(e.target.value)}
                placeholder="0"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">INM Cameras [Including object detection]</label>
              <input 
                type="number" 
                className="form-input" 
                value={inmCameras}
                onChange={(e) => setInmCameras(e.target.value)}
                placeholder="0"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Group</label>
              <select
                className="form-input"
                value={groupNumber}
                onChange={(e) => setGroupNumber(parseInt(e.target.value))}
              >
                {groupOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Processor Type</label>
              <select
                className="form-input"
                value={processorType}
                onChange={(e) => setProcessorType(e.target.value)}
              >
                <option value="intel">Intel (C6i)</option>
                <option value="amd">AMD (C6a)</option>
              </select>
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">Remaining Object Detection Cameras [without INM]</label>
            <input 
              type="text" 
              className="form-input readonly-input" 
              value={objDetectionFeatures}
              readOnly
            />
          </div>
          
          <button 
            className="calculate-btn"
            onClick={runCalculations}
            disabled={isCalculating}
          >
            {isCalculating ? (
              <span className="btn-loading">
                <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Calculating...
              </span>
            ) : "Calculate"}
          </button>
        </div>
        
        {/* Instance types reference */}
        {showInstanceTypes && (
          <div className="instance-types-section">
            <h3 className="section-title">
              AWS EC2 {processorType === 'intel' ? 'C6i (Intel)' : 'C6a (AMD)'} Family Instance Types
            </h3>
            
            <div className="table-container">
              <table className="instance-table">
                <thead>
                  <tr>
                    <th>Instance Size</th>
                    <th>vCPU</th>
                    <th>Memory (GiB)</th>
                  </tr>
                </thead>
                <tbody>
                  {getInstanceTypes().map((instance, index) => (
                    <tr key={instance.type}>
                      <td>{instance.type}</td>
                      <td>{instance.vCPUs}</td>
                      <td>{instance.memory}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="reference-link">
              <a 
                href={`https://aws.amazon.com/ec2/instance-types/${processorType === 'intel' ? 'c6i' : 'c6a'}/`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Official AWS EC2 {processorType === 'intel' ? 'C6i' : 'C6a'} Instance Types Reference
              </a>
            </div>
          </div>
        )}
        
        {/* Export buttons */}
        {results && (
          <div className="export-section">
            <button className="export-btn pdf-btn" onClick={exportToPDF}>
              Export to PDF
            </button>
            <button className="export-btn excel-btn" onClick={exportToExcel}>
              Export to Excel
            </button>
            {copyStatus && <span className="copy-status">{copyStatus}</span>}
          </div>
        )}
        
        {/* Results */}
        {results && (
          <div className="results-container">
            {/* LEVEL 1 */}
            <div className="level-section level-1">
              <h3 className="level-title">[Level -1] WITHOUT INM</h3>
              
              <div className="level-content">
                <div className="calculations-box">
                  <h4 className="box-title">Core Calculations</h4>
                  <div className="calc-details">
                    <div><span className="calc-label">Total Cameras:</span> {results.calculations.level1_totalCameras}</div>
                    <div><span className="calc-label">Object Detection Cameras:</span> {results.calculations.level1_objDetectionCameras}</div>
                    <div><span className="calc-label">Object Detection Pods:</span> {results.calculations.level1_objDetectionPodsRequired}</div>
                    <div><span className="calc-label">ZI, CD, Trunk Features Cores:</span> {results.calculations.level1_objDetectionCores.toFixed(2)}</div>
                    <div><span className="calc-label">Tamper Cores:</span> {results.calculations.level1_tamperCores.toFixed(2)}</div>
                    <div className="calc-total"><span className="calc-label">Total Cores:</span> {results.calculations.level1_totalCores.toFixed(2)}</div>
                    <div><span className="calc-label">Nodes Required:</span> {results.calculations.level1NodesRequired}</div>
                  </div>
                </div>
                
                <div className="instances-box">
                  <div className="box-header">
                    <h4 className="box-title">Instances</h4>
                    <button 
                      className="copy-btn"
                      onClick={() => handleCopy(results.recommendations.level1List.join('\n'))}
                    >
                      Copy
                    </button>
                  </div>
                  
                  <div className="instance-list">
                    {results.recommendations.level1List.map((instance, index) => (
                      <div key={index}>{instance}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* LEVEL 2 */}
            <div className="level-section level-2">
              <h3 className="level-title">[Level -2] WITH INM</h3>
              
              <div className="level-content">
                <div className="calculations-box">
                  <h4 className="box-title">Core Calculations</h4>
                  <div className="calc-details">
                    <div><span className="calc-label">Total Cameras:</span> {results.calculations.level2_totalCameras}</div>
                    <div><span className="calc-label">INM Cameras:</span> {results.calculations.inmCameras}</div>
                    <div><span className="calc-label">Object Detection Cameras:</span> {results.calculations.level2_objDetectionCameras}</div>
                    <div><span className="calc-label">INM Pods:</span> {results.calculations.inmPodsRequired}</div>
                    <div><span className="calc-label">INM Cores:</span> {results.calculations.inmCores.toFixed(2)}</div>
                    <div><span className="calc-label">Object Detection Pods:</span> {results.calculations.level2_objDetectionPodsRequired}</div>
                    <div><span className="calc-label">ZI, CD, Trunk Features Cores:</span> {results.calculations.level2_objDetectionCores.toFixed(2)}</div>
                    <div><span className="calc-label">Tamper Cores:</span> {results.calculations.level2_tamperCores.toFixed(2)}</div>
                    <div className="calc-total"><span className="calc-label">Cores Without Inference:</span> {results.calculations.totalCoresWithoutInference.toFixed(2)}</div>
                    <div><span className="calc-label">Approx Value:</span> {results.calculations.approxValue.toFixed(2)}</div>
                    <div><span className="calc-label">Inference 1 Cores:</span> {results.calculations.inference1Cores.toFixed(2)}</div>
                    <div><span className="calc-label">Inference 3 Cores:</span> {results.calculations.inference3Cores.toFixed(2)}</div>
                    <div><span className="calc-label">Triton Cores:</span> {results.calculations.tritionCores.toFixed(2)}</div>
                    <div><span className="calc-label">Kubernetes Cores:</span> {results.calculations.kubernetsCores.toFixed(2)}</div>
                    <div className="calc-total"><span className="calc-label">Total Cores:</span> {results.calculations.level2_totalCores.toFixed(2)}</div>
                    <div><span className="calc-label">Nodes Required:</span> {results.calculations.level2NodesRequired}</div>
                  </div>
                </div>
                
                <div className="instances-box">
                  <div className="box-header">
                    <h4 className="box-title">Instances</h4>
                    <button 
                      className="copy-btn"
                      onClick={() => handleCopy(results.recommendations.level2List.join('\n'))}
                    >
                      Copy
                    </button>
                  </div>
                  
                  <div className="instance-list">
                    {results.recommendations.level2List.map((instance, index) => (
                      <div key={index}>{instance}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* LEVEL 3 */}
            <div className="level-section level-3">
              <h3 className="level-title">[Level -3] Downscale Instances</h3>
              
              <div className="instances-box">
                <div className="box-header">
                  <h4 className="box-title">Instances (Master + Minimum 4 Slaves)</h4>
                  <button 
                    className="copy-btn"
                    onClick={() => handleCopy(results.recommendations.level3List.join('\n'))}
                  >
                    Copy
                  </button>
                </div>
                
                <div className="instance-list">
                  {results.recommendations.level3List.map((instance, index) => (
                    <div key={index}>{instance}</div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Technical info */}
            <div className="technical-info">
              <h3 className="section-title">Technical Information & Formulas</h3>
              <ul>
                <li><strong>Level 1 (WITHOUT INM):</strong> Uses total cameras but calculates WITHOUT INM features - only Object Detection + Tamper</li>
                <li><strong>Level 2 (WITH INM):</strong> Uses total cameras AND INM cameras - full calculation with all features + inference</li>
                <li><strong>Level 3 (DOWNSCALE):</strong> Minimum configuration to run the solution</li>
                <li><strong>INM Cores:</strong> INM Cameras × 2/3</li>
                <li><strong>Tamper Cores:</strong> Total Cameras × 2/6</li>
                <li><strong>ZI, CD, Trunk Features Cores:</strong> Total Cameras × 2/5 (applies to all cameras)</li>
                <li><strong>Total Cores Without Inference:</strong> INM Cores + Tamper Cores + ZI/CD/Trunk Cores</li>
                <li><strong>Approx Value:</strong> Total Cores Without Inference ÷ 128</li>
                <li><strong>Inference 1 Cores:</strong> 6 × Approx Value</li>
                <li><strong>Inference 3 Cores:</strong> 6 × Approx Value</li>
                <li><strong>Triton Servers Cores:</strong> 2 × Approx Value</li>
                <li><strong>Kubernetes Cores:</strong> 3 × Approx Value</li>
                <li><strong>Total Cores:</strong> Cores Without Inference + Inference 1 + Inference 3 + Triton + Kubernetes</li>
                <li><strong>Total Nodes Required:</strong> Total Cores ÷ 128</li>
                <li>Master node is always {getInstancePrefix()}.4xlarge</li>
                <li>Slaves can be larger than {getInstancePrefix()}.4xlarge but not smaller</li>
                <li>There are always at least 4 slave instances</li>
                <li><strong>C6a Differences:</strong> c6a.48xlarge (192 vCPUs, 384 GiB) and c6a.metal (192 vCPUs, 384 GiB) are different from C6i</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
